import { useEffect, useState } from 'react';
import { TrashIcon, WarningOctagonIcon } from '@phosphor-icons/react';
import { Story } from '@/types/cmsTypes';
import LureoButton from '@/common/LureoButton';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/cms/ui/alert-dialog";
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import type { components } from '@/generated/contracts/api-v1';

type SceneDto = components['schemas']['SceneDto'];

const PUBLISHED_STATUS_LABELS: Partial<Record<SceneDto['status'], string>> = {
  live: 'live, viewers can open it now',
  pending: 'in review',
  template: 'published as a template',
};

type PublishedVersions = { state: 'loading' } | { state: 'failed' } | { state: 'ready'; versions: SceneDto[] };

function usePublishedVersions(storyId: string | null): PublishedVersions {
  const [result, setResult] = useState<{ storyId: string; versions: PublishedVersions } | null>(null);
  // Closing clears the result, so reopening waits for a fresh check rather than showing the last one
  const [checkedFor, setCheckedFor] = useState(storyId);
  if (checkedFor !== storyId) {
    setCheckedFor(storyId);
    setResult(null);
  }

  useEffect(() => {
    if (!storyId) return;
    let cancelled = false;
    listAll(page => v1.GET('/api/v1/scenes', {
      params: { query: { storyUniqueId: [storyId], status: ['live', 'pending', 'template'], sort: 'versionNumber', ...page } },
    })).then(({ data, error }) => {
      if (cancelled) return;
      setResult({ storyId, versions: error ? { state: 'failed' } : { state: 'ready', versions: data } });
    });
    return () => { cancelled = true; };
  }, [storyId]);

  return result && result.storyId === storyId ? result.versions : { state: 'loading' };
}

type DeleteItemType = 'project' | 'scene';

interface DeleteItem {
  id: string;
  title: string;
  type: DeleteItemType;
}

interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (item: DeleteItem) => void;
  onCancel: () => void;
  item: DeleteItem | null;
  /** The story's public link, named in the warning list when deleting kills it (share links 3.12). */
  liveLinkUrl?: string | null;
  isLive?: boolean;
  liveLinkUnknown?: boolean;
}

export function DeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  item,
  liveLinkUrl,
  isLive = false,
  liveLinkUnknown = false
}: DeleteDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const published = usePublishedVersions(open && item?.type === 'scene' ? item.id : null);
  const checkingVersions = item?.type === 'scene' && published.state === 'loading';

  const handleConfirm = async () => {
    if (!item) return;

    setIsLoading(true);
    try {
      await onConfirm(item);
    } finally {
      setIsLoading(false);
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const getTitle = () => {
    if (!item) return '';
    return item.type === 'project' ? 'Delete Project' : 'Delete Story';
  };

  const getDescription = () => {
    if (!item) return '';
    return `Are you sure you want to delete "${item.title}"? This action cannot be undone.`;
  };

  const getWarningItems = () => {
    if (!item) return [];
    
    if (item.type === 'project') {
      return [
        'The project and all its content',
        'All associated stories',
        'Any published versions',
        'All media files and assets',
        'All version history'
      ];
    } else {
      const versionWarnings = published.state === 'failed'
        ? ['Any live or in-review version (we could not check which versions are published)']
        : published.state === 'ready'
          ? published.versions.map(version =>
              `${version.versionNumber ? `Version ${version.versionNumber}` : version.displayName}, which is ${PUBLISHED_STATUS_LABELS[version.status]}`)
          : [];
      return [
        ...versionWarnings,
        'The story and all its content',
        'Any associated media files',
        'All version history',
        ...(isLive ? ['Its live version, which your audience can open now'] : []),
        ...(liveLinkUrl
          ? [`Its public link ${liveLinkUrl}, which stops working for everyone who has it, on every copy sent or printed`]
          : liveLinkUnknown
            ? ['Its public link, which stops working for everyone who has it, on every copy sent or printed']
            : [])
      ];
    }
  };

  const getButtonText = () => {
    if (!item) return 'Delete';
    return isLoading 
      ? (item.type === 'project' ? 'Deleting Project...' : 'Deleting Story...')
      : (item.type === 'project' ? 'Delete Project' : 'Delete Story');
  };

  if (!open || !item) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2 select-none">
            <TrashIcon className="h-5 w-5 text-lureo-danger-ink" />
            <AlertDialogTitle>{getTitle()}</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            {getDescription()}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogBody className="select-none">
          <div className="flex items-center space-x-3 rounded-lureo-radius-lg bg-lureo-danger/5 p-4 border-lureo-border-width border-lureo-danger/30">
            <WarningOctagonIcon className="h-5 w-5 text-lureo-warning-ink shrink-0" />
            <div className="text-lureo-14 text-lureo-ink leading-5">
              <p className="font-medium">This will permanently delete:</p>
              <ul className="mt-1 list-disc list-inside space-y-1">
                {getWarningItems().map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </AlertDialogBody>

        <AlertDialogFooter>
          <LureoButton
            variant="outline-2"
            suppressTooltip
            onClick={handleCancel}
            className="h-9 px-5 rounded-lureo-radius-button-pill"
            disabled={isLoading}
          >
            Cancel
          </LureoButton>
          <LureoButton
            variant="danger"
            suppressTooltip
            onClick={handleConfirm}
            className="h-9 px-4 rounded-lureo-radius-button-pill"
            disabled={isLoading || checkingVersions}
          >
            {getButtonText()}
          </LureoButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Legacy export for backward compatibility
export function SceneDeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  story
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (story: Story) => void;
  onCancel: () => void;
  story: Story | null;
}) {
  const deleteItem: DeleteItem | null = story ? {
    id: story.id,
    title: story.title,
    type: 'scene'
  } : null;

  const handleConfirm = () => {
    if (story) {
      onConfirm(story);
    }
  };

  return (
    <DeleteDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={handleConfirm}
      onCancel={onCancel}
      item={deleteItem}
    />
  );
} 